import { useCallback, useEffect, useRef, useState } from 'react';

import config from '../config';
import Snackbar from './core/Snackbar';

const POLLING_INTERVAL = 60_000;
const BADGE_ID = 'ospitek-version-badge';

// ---------------------------------------------------------------------------
// Global version badge (Shift+V) — works even when ClientUpdater is not mounted.
// Registered once as a module side-effect so it's available on every kiosk page.
// ---------------------------------------------------------------------------
let badgeListenerRegistered = false;

if (typeof window !== 'undefined' && !badgeListenerRegistered) {
  badgeListenerRegistered = true;
  window.addEventListener('keydown', (e: KeyboardEvent) => {
    const target = e.target as HTMLElement;
    if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target.isContentEditable) {
      return;
    }
    if (e.shiftKey && e.key.toLowerCase() === 'v') {
      e.preventDefault();
      const existing = document.getElementById(BADGE_ID);
      if (existing) {
        existing.remove();
      } else {
        const badge = document.createElement('div');
        badge.id = BADGE_ID;
        badge.textContent = config.appVersion || 'dev';
        Object.assign(badge.style, {
          position: 'fixed',
          bottom: '8px',
          right: '8px',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          color: '#fff',
          fontSize: '11px',
          fontFamily: 'monospace',
          padding: '4px 8px',
          borderRadius: '4px',
          zIndex: '99999',
          pointerEvents: 'none',
        });
        document.body.appendChild(badge);
      }
    }
  });
}

// ---------------------------------------------------------------------------
// ClientUpdater — polls /version.json and triggers reload on SHA mismatch
// ---------------------------------------------------------------------------
function ClientUpdater() {
  const [updatingIn, setUpdatingIn] = useState<number | null>(null);
  const checkerRef = useRef<ReturnType<typeof setTimeout>>();
  const counterRef = useRef<ReturnType<typeof setTimeout>>();

  const countDown = useCallback((seconds: number) => {
    if (seconds <= 0) {
      window.location.reload();
      return;
    }
    setUpdatingIn(seconds);
    counterRef.current = setTimeout(() => countDown(seconds - 1), 1000);
  }, []);

  const checkForUpdate = useCallback(async () => {
    let newVersionAvailable: boolean | undefined;

    try {
      const response = await fetch(config.appURL + '/version.json', {
        method: 'GET',
        cache: 'no-store',
        headers: { pragma: 'no-cache', 'cache-control': 'no-cache' },
      });

      if (response.status === 200) {
        const data = await response.json();
        if (typeof data?.sha === 'string' && data.sha) {
          newVersionAvailable = data.sha !== config.appVersion;
        }
      } else {
        console.warn('Unexpected response when polling for current version.', response);
      }
    } catch (e) {
      console.error(e);
    }

    if (newVersionAvailable === true) {
      countDown(10);
    } else if (newVersionAvailable === false) {
      checkerRef.current = setTimeout(checkForUpdate, POLLING_INTERVAL);
    } else {
      // Error or non-200 — retry sooner
      checkerRef.current = setTimeout(checkForUpdate, POLLING_INTERVAL / 4);
    }
  }, [countDown]);

  useEffect(() => {
    checkerRef.current = setTimeout(checkForUpdate, POLLING_INTERVAL);
    return () => {
      clearTimeout(checkerRef.current);
      clearTimeout(counterRef.current);
    };
  }, [checkForUpdate]);

  if (updatingIn === null) return null;

  return (
    <Snackbar>
      {updatingIn > 0
        ? `The screen will be updated in ${updatingIn} second${updatingIn === 1 ? '' : 's'}.`
        : 'The screen will be updated any moment now.'}
    </Snackbar>
  );
}

export default config.appVersion ? ClientUpdater : () => null;
